/**
 * StreamingMarkdown — renders an actively-streaming AI reply with a
 * smoothed character reveal so bursty provider chunks don't make the
 * text pop in choppy blocks.
 *
 * Behavior:
 *   • While `streaming` is true, appends new characters via requestAnimationFrame
 *     at ~55 chars/sec (accelerated to 330 cps when the buffer is deep).
 *   • When `streaming` flips to false, snaps to the final full text so
 *     nothing is left half-revealed after the network says "done".
 *   • Delegates the actual formatting to ReactMarkdown so lists, code
 *     blocks, and links render properly.
 */
import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSmoothStream } from '../lib/useSmoothStream';

export default function StreamingMarkdown({ text = '', streaming = false, className = '' }) {
  const { visible, push, flush, reset } = useSmoothStream('');
  const lastLenRef = useRef(0);
  const wasStreamingRef = useRef(streaming);

  // On new streaming session, reset buffer. Detect via a text shrink or
  // a fresh streaming=true after a done state.
  useEffect(() => {
    if (text.length < lastLenRef.current) {
      reset('');
      lastLenRef.current = 0;
      if (text.length > 0) push(text);
      lastLenRef.current = text.length;
      return;
    }
    if (text.length > lastLenRef.current) {
      push(text.slice(lastLenRef.current));
      lastLenRef.current = text.length;
    }
  }, [text, push, reset]);

  // When streaming stops, flush any buffered characters immediately.
  useEffect(() => {
    if (wasStreamingRef.current && !streaming) {
      flush();
    }
    wasStreamingRef.current = streaming;
  }, [streaming, flush]);

  const display = streaming ? visible : text;
  return (
    <div className={`streaming-md ${className}`}>
      {display
        ? <ReactMarkdown>{display}</ReactMarkdown>
        : streaming
          ? <span className="text-cyan-sys animate-pulse">▍</span>
          : null}
      {streaming && display && (
        <span className="text-cyan-sys animate-pulse inline-block ml-0.5">▍</span>
      )}
    </div>
  );
}
